import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Plus, Save, ArrowLeft, Trash2 } from 'lucide-react';
import { useCompany } from '../context/CompanyContext';
import { formatCurrency, cn } from '../utils/formatters';
import { input, label, formGroup } from '../styles/components';
import type { BudgetItem } from '../types';

type EditorItem = { description: string; quantity: number; unitPrice: number };

export function BudgetEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { clients, createBudget, updateBudget, budgets } = useCompany();
  const budget = id ? budgets.find(b => b.id === id) : null;
  const isEdit = Boolean(budget);

  const [form, setForm] = useState({
    clientId: budget?.clientId || '',
    issueDate: budget?.issueDate?.split('T')[0] || new Date().toISOString().split('T')[0],
    notes: budget?.notes || '',
    vatRate: budget?.vatRate || 21,
  });

  const [items, setItems] = useState<EditorItem[]>(
    budget && budget.items.length > 0
      ? budget.items.map(i => ({ description: i.description, quantity: i.quantity, unitPrice: i.unitPrice }))
      : [{ description: '', quantity: 1, unitPrice: 0 }]
  );

  const subtotal = items.reduce((s, i) => s + i.quantity * i.unitPrice, 0);
  const vatAmount = (subtotal * form.vatRate) / 100;
  const total = subtotal + vatAmount;

  const addItem = () => setItems([...items, { description: '', quantity: 1, unitPrice: 0 }]);
  const removeItem = (idx: number) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx: number, patch: Partial<EditorItem>) => {
    setItems(items.map((item, i) => (i === idx ? { ...item, ...patch } : item)));
  };

  const buildItems = (): BudgetItem[] =>
    items.map(i => ({
      id: crypto.randomUUID(),
      description: i.description,
      category: 'material' as const,
      quantity: i.quantity,
      unitPrice: i.unitPrice,
      total: i.quantity * i.unitPrice,
    }));

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const expiry = new Date(form.issueDate);
    expiry.setDate(expiry.getDate() + 30);

    if (isEdit && budget) {
      await updateBudget(budget.id, {
        clientId: form.clientId,
        issueDate: form.issueDate,
        items: buildItems(),
        vatRate: form.vatRate,
        notes: form.notes,
      });
    } else {
      await createBudget({
        clientId: form.clientId,
        issueDate: form.issueDate,
        expiryDate: expiry.toISOString().split('T')[0],
        status: 'draft',
        items: buildItems(),
        extraItems: [],
        notes: form.notes,
        vatRate: form.vatRate,
      });
    }
    navigate('/budgets');
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/budgets" className="b2b-btn b2b-btn--ghost b2b-btn--sm">
          <ArrowLeft className="w-4 h-4" />
          Volver
        </Link>
        <div className="space-y-1">
          <span className="b2b-eyebrow">Presupuesto</span>
          <h1 className="text-2xl font-extrabold tracking-tight">
            {isEdit ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}
          </h1>
        </div>
      </div>

      <div className="b2b-panel p-6">
        <form onSubmit={save} className="space-y-4">
          <div className={formGroup}>
            <label htmlFor="client" className={label}>Cliente *</label>
            <select id="client" className={input} value={form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })} required>
              <option value="">Seleccionar cliente</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className={formGroup}>
              <label htmlFor="date" className={label}>Fecha emisión *</label>
              <input id="date" type="date" className={input} value={form.issueDate} onChange={e => setForm({ ...form, issueDate: e.target.value })} required />
            </div>
            <div className={formGroup}>
              <label htmlFor="vatRate" className={label}>IVA</label>
              <select id="vatRate" className={input} value={form.vatRate} onChange={e => setForm({ ...form, vatRate: parseInt(e.target.value) })}>
                <option value={21}>21% (General)</option>
                <option value={10}>10% (Rehabilitación)</option>
              </select>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-2">Conceptos</h3>
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    type="text"
                    className={cn(input, 'col-span-6')}
                    placeholder="Descripción"
                    value={item.description}
                    onChange={e => updateItem(idx, { description: e.target.value })}
                  />
                  <input
                    type="number"
                    className={cn(input, 'col-span-2')}
                    placeholder="Cant."
                    value={item.quantity}
                    onChange={e => updateItem(idx, { quantity: parseInt(e.target.value) || 0 })}
                  />
                  <input
                    type="number"
                    className={cn(input, 'col-span-3')}
                    placeholder="Precio"
                    value={item.unitPrice}
                    onChange={e => updateItem(idx, { unitPrice: parseFloat(e.target.value) || 0 })}
                  />
                  <button type="button" onClick={() => removeItem(idx)} className="b2b-btn b2b-btn--ghost b2b-btn--sm col-span-1 px-2" aria-label="Eliminar concepto">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" onClick={addItem} className="b2b-btn b2b-btn--ghost b2b-btn--sm mt-2">
              <Plus className="w-4 h-4" /> Añadir concepto
            </button>
          </div>

          <div className={formGroup}>
            <label htmlFor="notes" className={label}>Notas</label>
            <textarea id="notes" className={cn(input, 'min-h-[80px]')} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>

          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-[var(--b2b-border)]">
            <div>
              <p className="text-sm b2b-muted">Subtotal</p>
              <p className="text-xl font-bold">{formatCurrency(subtotal)}</p>
            </div>
            <div>
              <p className="text-sm b2b-muted">IVA ({form.vatRate}%)</p>
              <p className="text-xl font-bold">{formatCurrency(vatAmount)}</p>
            </div>
            <div>
              <p className="text-sm b2b-muted">Total</p>
              <p className="text-2xl font-bold text-[color:var(--b2b-accent-strong)]">{formatCurrency(total)}</p>
            </div>
          </div>

          <div className="flex gap-3 pt-6">
            <button type="submit" className="b2b-btn b2b-btn--primary">
              <Save className="w-4 h-4" />
              Guardar Presupuesto
            </button>
            <Link to="/budgets" className="b2b-btn b2b-btn--ghost">Cancelar</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
